import { useState } from 'react';
import { CalendarClock, Check, Package, Send, X } from 'lucide-react';
import {
  OrderStatus,
  PARCEL_APPROVAL_LABELS,
  PARCEL_PAYER_LABELS,
  PARCEL_SIZE_LABELS,
  ParcelApproval,
} from '@kick/types';
import { getErrorMessage } from '@/lib/api';
import { usePuede } from '@/lib/permisos';
import { cn, formatCOP, formatDistance } from '@/lib/cn';
import {
  useDecidirEncomienda,
  useDespacharEncomienda,
  useEncomiendasProgramadas,
  type EncomiendaProgramada,
} from '@/lib/queries';
import { Boton, Cargando, Entrada, ErrorCarga, Modal, Vacio } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

/** Cómo se lee una fecha programada en Medellín. */
function cuando(iso: string): string {
  return new Date(iso).toLocaleString('es-CO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Bogota',
  });
}

/** Cuánto falta, que es lo que decide la urgencia de mirarla. */
function faltan(iso: string): string {
  const horas = (new Date(iso).getTime() - Date.now()) / 3_600_000;
  if (horas < 0) return 'ya pasó';
  if (horas < 1) return `en ${Math.round(horas * 60)} min`;
  if (horas < 24) return `en ${Math.round(horas)} h`;
  return `en ${Math.round(horas / 24)} días`;
}

const COLOR: Record<ParcelApproval, string> = {
  [ParcelApproval.PENDING]: 'bg-amber-100 text-amber-900',
  [ParcelApproval.APPROVED]: 'bg-green-100 text-green-900',
  [ParcelApproval.REJECTED]: 'bg-red-100 text-red-900',
  [ParcelApproval.AWAITING_REPRICE]: 'bg-indigo-100 text-indigo-900',
};

/**
 * Encomiendas programadas, esperando decisión.
 *
 * Comprometerse a recoger algo el jueves es una promesa: alguien tiene que
 * poder mirarla antes de que se haga, y ver de un vistazo si hay cobertura ese
 * día y si el encargo es razonable. Las más próximas van arriba porque son las
 * que menos margen dejan para decidir.
 */
export function Programadas() {
  const puede = usePuede();
  const [todas, setTodas] = useState(false);
  const lista = useEncomiendasProgramadas(todas);
  const [rechazando, setRechazando] = useState<EncomiendaProgramada | null>(null);

  if (lista.isLoading) return <Cargando texto="Cargando las programadas…" />;
  if (lista.isError) {
    return (
      <ErrorCarga mensaje={getErrorMessage(lista.error)} onReintentar={() => void lista.refetch()} />
    );
  }

  const encomiendas = lista.data ?? [];
  const pendientes = encomiendas.filter((e) => e.approval === ParcelApproval.PENDING).length;

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl text-kick-dark">Encomiendas programadas</h1>
          <p className="mt-1 text-sm text-kick-gray">
            {pendientes > 0
              ? `${pendientes} ${pendientes === 1 ? 'espera' : 'esperan'} tu decisión. Las más próximas primero.`
              : 'Nada pendiente de decidir.'}
          </p>
        </div>
        <button
          onClick={() => setTodas((v) => !v)}
          aria-pressed={todas}
          className={cn(
            'rounded-kick border px-3 py-1.5 text-sm font-semibold transition',
            todas
              ? 'border-kick-red bg-kick-red text-white'
              : 'border-kick-gray/25 text-kick-ink hover:border-kick-gray/50',
          )}
        >
          Ver también las decididas
        </button>
      </div>

      {encomiendas.length === 0 ? (
        <div className="mt-6">
          <Vacio
            icono={<CalendarClock className="h-6 w-6" />}
            titulo={todas ? 'Ninguna programada' : 'Nada por decidir'}
            descripcion={
              todas
                ? 'Nadie ha programado una encomienda todavía.'
                : 'Todas las programadas ya tienen decisión.'
            }
          />
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {encomiendas.map((e) => (
            <Tarjeta
              key={e.orderId}
              encomienda={e}
              puedeDecidir={puede('programadas', 'decidir')}
              onRechazar={() => setRechazando(e)}
            />
          ))}
        </div>
      )}

      <ModalRechazo encomienda={rechazando} onCerrar={() => setRechazando(null)} />
    </div>
  );
}

function Tarjeta({
  encomienda: e,
  puedeDecidir,
  onRechazar,
}: {
  encomienda: EncomiendaProgramada;
  /** Sin el permiso de decidir, la tarjeta es de solo lectura. */
  puedeDecidir: boolean;
  onRechazar: () => void;
}) {
  const decidir = useDecidirEncomienda();
  const despachar = useDespacharEncomienda();
  const avisos = useAvisos();

  const pendiente = e.approval === ParcelApproval.PENDING;
  const aprobada = e.approval === ParcelApproval.APPROVED && e.status === OrderStatus.SCHEDULED;

  return (
    <article className="rounded-kick bg-white p-5 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Package className="h-4 w-4 text-kick-gray" />
            <span className="font-mono font-bold text-kick-dark">{e.code}</span>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-xs font-semibold',
                COLOR[e.approval],
              )}
            >
              {PARCEL_APPROVAL_LABELS[e.approval]}
            </span>
          </div>

          <p className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-kick-ink">
            <CalendarClock className="h-4 w-4 text-kick-red" />
            {cuando(e.scheduledFor)}
            <span className="font-normal text-kick-gray">· {faltan(e.scheduledFor)}</span>
          </p>

          <p className="mt-1 text-sm text-kick-gray">
            {e.pickupAddress.addressLine} → {e.deliveryAddress.addressLine}
            {' · '}
            {formatDistance(e.distanceMeters)}
          </p>
        </div>

        <div className="text-right">
          <p className="text-xl font-bold text-kick-dark">{formatCOP(e.total)}</p>
          <p className="text-xs text-kick-gray">{e.customerName}</p>
        </div>
      </div>

      <dl className="mt-4 grid gap-x-6 gap-y-1 border-t border-kick-gray/15 pt-3 text-sm sm:grid-cols-2">
        <Dato etiqueta="Qué" valor={e.description} />
        <Dato etiqueta="Tamaño" valor={PARCEL_SIZE_LABELS[e.size]} />
        <Dato etiqueta="Lo recibe" valor={`${e.recipientName} · ${e.recipientPhone}`} />
        <Dato etiqueta="Paga" valor={PARCEL_PAYER_LABELS[e.payer]} />
        {!!e.declaredValue && (
          <Dato etiqueta="Valor declarado" valor={formatCOP(e.declaredValue)} />
        )}
        {e.approval === ParcelApproval.AWAITING_REPRICE && e.repricedTotal !== null && (
          <Dato
            etiqueta="Precio nuevo por confirmar"
            valor={`${formatCOP(e.repricedTotal)} (prometido ${formatCOP(e.quotedTotal ?? e.total)})`}
          />
        )}
        {!!e.decisionNote && <Dato etiqueta="Nota" valor={e.decisionNote} />}
      </dl>

      {(pendiente || aprobada) && puedeDecidir && (
        <div className="mt-4 flex flex-wrap gap-2">
          {pendiente && (
            <>
              <Boton
                cargando={decidir.isPending}
                icono={<Check className="h-4 w-4" />}
                onClick={() =>
                  decidir.mutate(
                    { orderId: e.orderId, approve: true },
                    {
                      onSuccess: () => avisos.exito(`${e.code} confirmada`),
                      onError: (err) => avisos.error(getErrorMessage(err)),
                    },
                  )
                }
              >
                Aprobar
              </Boton>
              <Boton variante="peligro" icono={<X className="h-4 w-4" />} onClick={onRechazar}>
                Rechazar
              </Boton>
            </>
          )}

          {aprobada && (
            <Boton
              variante="secundario"
              cargando={despachar.isPending}
              icono={<Send className="h-4 w-4" />}
              onClick={() =>
                despachar.mutate(e.orderId, {
                  onSuccess: () => avisos.exito(`${e.code} despachada`),
                  onError: (err) => avisos.error(getErrorMessage(err)),
                })
              }
            >
              Sacarla ya
            </Boton>
          )}
        </div>
      )}
    </article>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="flex gap-2">
      <dt className="shrink-0 text-kick-gray">{etiqueta}:</dt>
      <dd className="min-w-0 flex-1 text-kick-ink">{valor}</dd>
    </div>
  );
}

/**
 * Rechazar exige un motivo, y el motivo llega tal cual al cliente.
 *
 * Un «no se puede» sin explicación deja a alguien con un paquete en la mano y
 * sin saber qué hacer con él; escribir una línea cuesta menos que la llamada
 * que vendría después.
 */
function ModalRechazo({
  encomienda,
  onCerrar,
}: {
  encomienda: EncomiendaProgramada | null;
  onCerrar: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const decidir = useDecidirEncomienda();
  const avisos = useAvisos();

  return (
    <Modal
      abierto={!!encomienda}
      onCerrar={onCerrar}
      titulo={`Rechazar ${encomienda?.code ?? ''}`}
      ancho="sm"
    >
      <div className="space-y-4 p-5">
        <p className="text-sm text-kick-gray">
          El cliente recibe este mensaje tal cual, y la encomienda queda cancelada.
        </p>

        <Entrada
          value={motivo}
          onChange={(ev) => setMotivo(ev.target.value)}
          maxLength={300}
          placeholder="Ej: ese día no tenemos cobertura en esa zona"
          aria-label="Motivo del rechazo"
        />

        <div className="flex gap-2">
          <Boton variante="secundario" className="flex-1" onClick={onCerrar}>
            Volver
          </Boton>
          <Boton
            variante="peligro"
            className="flex-1"
            disabled={!motivo.trim()}
            cargando={decidir.isPending}
            onClick={() =>
              encomienda &&
              decidir.mutate(
                { orderId: encomienda.orderId, approve: false, note: motivo.trim() },
                {
                  onSuccess: () => {
                    avisos.exito(`${encomienda.code} rechazada`);
                    setMotivo('');
                    onCerrar();
                  },
                  onError: (err) => avisos.error(getErrorMessage(err)),
                },
              )
            }
          >
            Rechazar
          </Boton>
        </div>
      </div>
    </Modal>
  );
}
